import { useEffect, useState, useRef } from "react";
import { USERS, CREATE_USER, CREATE_USERS, UPDATE_USER } from "../graphQueries";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Table,
  Loader,
  Button,
  Header,
  Label,
  Popup,
  Dropdown,
  Input,
  Icon,
} from "semantic-ui-react";
import PageModal from "../PageModal/PageModal";
import AddEditUser from "./AddEditUser/AddEditUser";
import { ReactSpreadsheetImport } from "react-spreadsheet-import";
import { importFields } from "./importUserFields";

import { toast } from "react-toastify";
import moment from "moment";
import Widget from "../Common/Widget";

const Users = () => {
  const [usersList, setUsersList] = useState([]);
  const [initializing, setInit] = useState(true);
  const [isAddEditUser, setAddEditUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState({});
  const [mutating, setMutating] = useState(false);
  const [mode, setMode] = useState("add");
  const [isImport, setImport] = useState(false);
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [filteredList, setFiltered] = useState([]);
  const [filter, setFilter] = useState("");
  const [searching, setSearching] = useState(false);
  const [recordsDisplayed, setRecordsDisplayed] = useState(0);
  const [sort, setSort] = useState({
    column: "createdAt",
    order: "descending",
  });

  const clearTimerRef = useRef();

  useEffect(() => {
    users();
  }, []);

  useEffect(() => {
    handleFilter();
  }, [filter]);

  useEffect(() => {
    if (usersList.length) {
      setInit(false);
    }
  }, [usersList]);

  const [users] = useLazyQuery(USERS, {
    fetchPolicy: "network-only",
    onCompleted: (result) => handleUsers_Result(result),
    onError: (error) => handleUsers_Error(error),
  });

  const handleUsers_Result = (result) => {
    console.log(result);
    if (result.users) {
      let array = result.users.map((item) => Object.assign({}, item, {}));
      setUsersList(array);
      setNumPages(Math.ceil(array.length / 50));
      setRecordsDisplayed(array.length);
    }
  };

  const handleUsers_Error = (error) => {
    console.log(error);
  };

  const handleChangePage = (direction) => {
    if (direction === "up") {
      setPage(page + 1);
    }
    if (direction === "down") {
      setPage(page - 1);
    }
    const scrollingContainer = document.getElementById("scrollingContainer");
    if (scrollingContainer) {
      scrollingContainer.scrollTo({
        top: 0,
        left: 0,
      });
    }
  };

  const getPages = () => {
    let arrayOfPages = [];
    let count = 1;
    while (count <= numPages) {
      const obj = {
        text: count,
        value: count,
      };
      arrayOfPages.push(obj);
      count++;
    }
    // console.log(arrayOfPages);
    return arrayOfPages;
  };

  const paginate = (array, page_size, page_number) => {
    // human-readable page numbers usually start with 1, so we reduce 1 in the first argument
    return array.slice((page_number - 1) * page_size, page_number * page_size);
  };

  const handleChangeField = (e, data) => {
    const key = data.id;
    let value = e.target.value;
    if (data.id === "role") value = data.value;
    let obj = { ...selectedUser };
    Object.assign(obj, { [key]: value });
    setSelectedUser(obj);
  };

  const handleAddUser = () => {
    setMutating(true);
    if (selectedUser.email && selectedUser.role) {
      createUser({
        variables: selectedUser,
      });
    } else {
      setMutating(false);
      toast.error("Please fill out all required fields.");
    }
  };

  const [createUser] = useMutation(CREATE_USER, {
    onCompleted: (result) => handleCreateUser_Result(result),
    onError: (error) => handleCreateUser_Error(error),
  });

  const handleCreateUser_Result = (result) => {
    console.log(result);
    if (result.createUser.status === "succeeded") {
      console.log("inside result");
      toast.success("User created successfully.");
      users();
      resetState();
    } else {
      toast.error("Error creating user.");
      resetState();
    }
  };

  const handleCreateUser_Error = (error) => {
    console.log(error);
    toast.error(error.message);
    resetState();
  };

  const resetState = () => {
    setTimeout(() => {
      setMutating(false);
      setAddEditUser(false);
      setSelectedUser("");
    }, 3000);
  };

  const handleSetAddUser = () => {
    setSelectedUser({});
    setAddEditUser(true);
    setMode("add");
  };

  const handleSetEditUser = () => {
    setAddEditUser(true);
    setMode("edit");
  };

  const handleUpdateUser = () => {
    setMutating(true);
    console.log(selectedUser);
    updateUser({ variables: selectedUser });
  };

  const [updateUser] = useMutation(UPDATE_USER, {
    onCompleted: (result) => handleUpdateUser_Result(result),
    onError: (error) => handleUpdateUser_Error(error),
  });

  const handleUpdateUser_Result = (result) => {
    console.log(result);
    if (result.updateUser.status === "succeeded") {
      toast.success("User updated successfully!");
      users();
      resetState();
    }
    if (result.updateUser.status === "deleted") {
      toast.success("User deleted successfully!");
      users();
      resetState();
    }

    if (result.updateUser.status === "failed") {
      toast.error(result.updateUser.message || "Unable to update user");
      users();
      resetState();
    }
  };

  const handleUpdateUser_Error = (error) => {
    console.log(error);
    toast.error(error.message);
    resetState();
  };

  const handleDisableUser = () => {
    const obj = { ...selectedUser };
    Object.assign(obj, { status: "Disabled" });
    updateUser({ variables: obj });
  };

  const handleDeleteUser = () => {
    const obj = { ...selectedUser };
    Object.assign(obj, { status: "Deleted" });
    updateUser({ variables: obj });
  };

  const getEditDisabled = () => {
    if (selectedUser.userId) return false;
    else return true;
  };

  const getStatusColumn = (status) => {
    if (status === "Active") {
      return <Label color="green" content="Active" />;
    }
    if (status === "Disabled") {
      return <Label color="orange" content="Disabled" />;
    }
    if (status === "Pending") {
      return <Label color="blue" content="Pending" />;
    }
  };

  const handleFilter = () => {
    setSearching(true);
    const searchTerm = filter.toUpperCase();
    const termsArray = searchTerm.split(" ");
    console.log(termsArray);
    let filteredArray = [...usersList];
    for (const t of termsArray) {
      filteredArray = filteredArray.filter((x) =>
        `${x.fullName} ${x.email} ${x.zipCode} ${x.role} ${x.status}`
          .toUpperCase()
          .includes(t)
      );
    }

    clearTimeout(clearTimerRef.current);
    clearTimerRef.current = setTimeout(() => {
      setSearching(false);
      if (searchTerm === "") {
        setFiltered([]);
      } else {
        setFiltered(filteredArray);
      }
    }, 500);
  };

  const handleSort = (column) => {
    const newOrder = sort.order === "ascending" ? "descending" : "ascending";
    let sortList = [...usersList];
    if (filteredList && filteredList.length > 0) {
      sortList = [...filteredList];
    }
    if (newOrder === "ascending") {
      sortList.sort((a, b) => (a[column] || "").localeCompare(b[column] || ""));
    }
    if (newOrder === "descending") {
      sortList.sort((a, b) => (b[column] || "").localeCompare(a[column] || ""));
    }
    if (filteredList && filteredList.length > 0) {
      setFiltered(sortList);
    } else {
      setUsersList(sortList);
    }

    setSort({ column, order: newOrder });
  };

  const handleSortByTime = (column) => {
    const newOrder = sort.order === "ascending" ? "descending" : "ascending";
    let sortList = [...usersList];
    if (newOrder === "ascending") {
      sortList.sort((a, b) => new Date(a[column]) - new Date(b[column]));
    }
    if (newOrder === "descending") {
      sortList.sort((a, b) => new Date(b[column]) - new Date(a[column]));
    }
    setUsersList(sortList);
    setSort({ column, order: newOrder });
  };

  const handleImportSubmit = (data) => {
    console.log(data);
    const array = data.validData;
    createUsers({ variables: { userInfo: array } });
  };

  const [createUsers] = useMutation(CREATE_USERS, {
    onCompleted: (result) => handleCreateUsers_Result(result),
    onError: (error) => handleCreateUsers_Error(error),
  });

  const handleCreateUsers_Result = (result) => {
    console.log(result);
  };

  const handleCreateUsers_Error = (error) => {
    console.log(error);
  };

  const getPayoutStatus = (stripeAcctObj) => {
    if (stripeAcctObj === null) return "-";
    const obj = JSON.parse(stripeAcctObj);
    if (obj.payoutsEnabled) {
      return <Label content="Enabled" color="green" />;
    } else {
      return <Label content="Disabled" color="red" />;
    }
  };

  const UsersList = () => {
    let users = usersList;
    if (filteredList && filteredList.length > 0) {
      users = filteredList;
    }
    setNumPages(Math.ceil(users.length / 50));
    setRecordsDisplayed(users.length);
    const paged = paginate(users, 50, page);
    return paged.map((item, index) => {
      return (
        <Table.Row
          key={index}
          active={selectedUser.userId === item.userId}
          onClick={() => setSelectedUser(item)}
        >
          <Table.Cell>
            {moment(new Date(item.createdAt)).format("LLL")}
          </Table.Cell>
          <Table.Cell>{item.fullName}</Table.Cell>
          <Table.Cell>{item.email}</Table.Cell>
          <Table.Cell>{item.role}</Table.Cell>
          <Table.Cell>{getStatusColumn(item.status)}</Table.Cell>
          <Table.Cell>{getPayoutStatus(item.stripeAccount)}</Table.Cell>
          <Table.Cell>
            {item.lastLogin
              ? moment(new Date(item.lastLogin)).format("LLL")
              : "-"}
          </Table.Cell>
        </Table.Row>
      );
    });
  };
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        height: "calc(100% - 50px)",
        top: "50px",
        position: "absolute",
        backgroundColor: "#ffffff",
      }}
    >
      <div
        style={{
          backgroundColor: "#ffffff",
          width: "100%",
          height: "100%",
          display: "flex",
          padding: 20,
        }}
      >
        <div
          style={{
            flex: 1,
            transition: "flex 0.3s ease",
            // Optionally add border/padding
          }}
        >
          {/* HEADER AND SEARCH */}
          <Header
            as="h1"
            content="Users"
            subheader="Add new or Edit existing users."
          />
          <Input
            size="large"
            style={{ margin: "16px 0", width: "100%" }}
            placeholder="Search by Name, Email, Role, Location..."
            icon={
              searching ? (
                <Icon loading name="circle notch" />
              ) : (
                <Icon name="search" />
              )
            }
            iconPosition="left"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            action={
              filter ? (
                <Button
                  content="Clear Search"
                  icon="close"
                  secondary={filter}
                  disabled={!filter}
                  onClick={() => setFilter("")}
                />
              ) : null
            }
          />

          {/* --- SCROLLABLE TABLE --- */}
          <div
            className="table-container"
            style={{
              overflowY: "auto",
              maxHeight: "calc(100vh - 320px)", // Adjust based on header/search height + desired margin
              borderRadius: 5,
              border: "1px solid #D5D5D5",
              background: "#fff",
            }}
          >
            <Table selectable sortable style={{ border: "none" }}>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell
                    sorted={sort.column === "createdAt" ? sort.order : null}
                    onClick={() => handleSortByTime("createdAt")}
                  >
                    Created
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sorted={sort.column === "fullName" ? sort.order : null}
                    onClick={() => handleSort("fullName")}
                  >
                    Name
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sorted={sort.column === "email" ? sort.order : null}
                    onClick={() => handleSort("email")}
                  >
                    Email
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sorted={sort.column === "role" ? sort.order : null}
                    onClick={() => handleSort("role")}
                  >
                    Role
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sorted={sort.column === "status" ? sort.order : null}
                    onClick={() => handleSort("status")}
                  >
                    Status
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sorted={sort.column === "payouts" ? sort.order : null}
                    onClick={() => handleSort("payouts")}
                  >
                    Payouts
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sorted={sort.column === "lastLogin" ? sort.order : null}
                    onClick={() => handleSortByTime("lastLogin")}
                  >
                    Last Login
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                <UsersList />
              </Table.Body>
            </Table>
            {initializing && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 150,
                  width: "100%",
                }}
              >
                <Loader size="large" content="Loading Users..." active />
              </div>
            )}
          </div>

          {/* MODALS, ETC */}
          <PageModal
            noScroll
            heading={mode === "add" ? "Add User" : "Edit User"}
            open={isAddEditUser}
            setOpen={setAddEditUser}
            actions={
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Button
                  size="large"
                  content="Close"
                  onClick={() => setAddEditUser(false)}
                />
                <Button
                  loading={mutating}
                  disabled={mutating}
                  size="large"
                  content={mode === "add" ? "Add" : "Update"}
                  positive
                  onClick={mode === "add" ? handleAddUser : handleUpdateUser}
                />
              </div>
            }
          >
            <AddEditUser
              selectedUser={selectedUser}
              onChangeField={handleChangeField}
            />
          </PageModal>
          {isImport && (
            <ReactSpreadsheetImport
              isOpen={isImport}
              onClose={() => setImport(false)}
              onSubmit={handleImportSubmit}
              fields={importFields}
            />
          )}

          {/* FIXED FOOTER BAR */}
          <div
            style={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 20,
              background:
                "linear-gradient(to top, rgba(241,241,241,0.98) 80%, rgba(241,241,241,0.0))",
              padding: "1.5rem 0 1.5rem 0",
              display: "flex",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                width: "100%",
                pointerEvents: "auto",
                padding: "0 2rem",
                display: "flex",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <div>
                <Button
                  size="large"
                  disabled={page === 1}
                  icon="left arrow"
                  onClick={() => handleChangePage("down")}
                />
                <Dropdown
                  style={{ marginRight: "5px" }}
                  selection
                  compact
                  scrolling
                  options={getPages()}
                  onChange={(e, data) => setPage(data.value)}
                  value={page}
                />
                <Button
                  size="large"
                  disabled={page === numPages}
                  icon="right arrow"
                  onClick={() => handleChangePage("up")}
                />
                <span style={{ marginLeft: 10 }}>
                  {page === 1 ? 1 : ((page - 1) * 50 + 1).toLocaleString()} to{" "}
                  {page * 50 + 1 > recordsDisplayed
                    ? recordsDisplayed.toLocaleString()
                    : (page * 50 + 1).toLocaleString()}{" "}
                  <b>({recordsDisplayed.toLocaleString()} total)</b>
                </span>
              </div>
              <div>
                <Popup
                  on="click"
                  content={
                    <Button
                      secondary
                      content="Really Delete?"
                      onClick={handleDeleteUser}
                    />
                  }
                  trigger={
                    <Button
                      title="Permanently delete the user."
                      disabled={getEditDisabled()}
                      size="large"
                      icon="ban"
                      content="Delete"
                      color="red"
                    />
                  }
                />
                <Button
                  title="Disabled users are unable to login."
                  disabled={getEditDisabled()}
                  size="large"
                  icon="minus"
                  content="Disable"
                  color="orange"
                  onClick={handleDisableUser}
                />
                <Button
                  disabled={getEditDisabled()}
                  size="large"
                  icon="edit"
                  content="Edit"
                  primary
                  onClick={handleSetEditUser}
                />
                <Button
                  size="large"
                  icon="plus"
                  content="Add"
                  positive
                  onClick={handleSetAddUser}
                />
                <Button
                  size="large"
                  icon="upload"
                  content="Import"
                  positive
                  onClick={() => setImport(true)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Users;
